import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  FileText,
  CheckCircle,
  Clock,
  Download,
  Printer,
  Euro,
  Calendar as CalendarIcon,
  Users,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Plus,
  X,
  Save,
  RefreshCw,
  Eye,
  FileSpreadsheet
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;

const MyBillings = () => {
  const navigate = useNavigate();
  const [workDays, setWorkDays] = useState([]);
  const [billings, setBillings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Statistics for header
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    rejected: 0,
    pending: 0,
    totalAmount: 0
  });
  
  // New states for billing modal
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [billingModalData, setBillingModalData] = useState(null);
  const [loadingBillingData, setLoadingBillingData] = useState(false);
  const [billingOrders, setBillingOrders] = useState([]);
  const [billingPositions, setBillingPositions] = useState([]);
  const [orderGroups, setOrderGroups] = useState([]);

  useEffect(() => {
    loadMonthData();
    loadEmployeeId();
  }, [currentDate]);
  
  const loadEmployeeId = async () => {
    try {
      const token = localStorage.getItem('authToken');
      // Use /auth/me instead of /auth/permissions to get employee_id
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.employee_id) {
        localStorage.setItem('employee_id', response.data.employee_id);
      } else if (response.data.id) {
        // Fallback: Use user id as employee_id
        localStorage.setItem('employee_id', response.data.id);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Employee ID:', error);
    }
  };

  const loadMonthData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;

      // Load work days and billings in parallel
      const [workDaysResp, billingsResp] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/calendar/my-work-days`, {
          params: { year, month },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/billings`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      console.log('📅 Loaded work days:', workDaysResp.data);
      console.log('📅 Work days count:', workDaysResp.data.length);
      setWorkDays(workDaysResp.data);
      
      // Filter billings for current month
      const currentMonthBillings = billingsResp.data.filter(billing => {
        const billingDate = new Date(billing.date);
        return billingDate.getMonth() === currentDate.getMonth() && 
               billingDate.getFullYear() === currentDate.getFullYear();
      });
      
      console.log('💰 Loaded billings:', currentMonthBillings);
      console.log('💰 Billings count:', currentMonthBillings.length);
      setBillings(currentMonthBillings);
      
      // Calculate statistics - "Offen" includes both submitted and draft
      const statistics = {
        total: currentMonthBillings.length,
        approved: currentMonthBillings.filter(b => b.status === 'approved').length,
        rejected: currentMonthBillings.filter(b => b.status === 'rejected').length,
        pending: currentMonthBillings.filter(b => b.status === 'submitted' || b.status === 'draft').length,
        totalAmount: currentMonthBillings.reduce((sum, b) => sum + (b.total_amount || 0), 0)
      };
      
      setStats(statistics);
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
    } finally {
      setLoading(false);
    }
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleRefresh = () => {
    loadMonthData();
  };
  
  const exportToPDF = async (billing) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/billings/${billing.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `abrechnung_${billing.date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Fehler beim PDF-Export:', error);
      alert('Fehler beim PDF-Export');
    }
  };
  
  const exportToExcel = async (billing) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/billings/${billing.id}/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `abrechnung_${billing.date}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Fehler beim Excel-Export:', error);
      alert('Fehler beim Excel-Export');
    }
  };
  
  const resubmitBilling = async (billing) => {
    // Open the billing modal in edit mode
    createBilling(billing.date);
  };

  const getBillingForDate = (dateStr) => {
    return billings.find(b => b.date === dateStr);
  };

  const printBilling = (billing) => {
    setSelectedBilling(billing);
    setShowDetailModal(true);
    
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const openDetails = (billing) => {
    setSelectedBilling(billing);
    setShowDetailModal(true);
  };

  const createBilling = async (dateStr) => {
    // Load day details for this specific date
    setSelectedDate(dateStr);
    setShowBillingModal(true);
    setLoadingBillingData(true);
    setBillingOrders([]);
    setOrderGroups([]);
    
    try {
      const token = localStorage.getItem('authToken');
      
      // Check if there's an existing billing for this date
      const existingBilling = billings.find(b => b.date === dateStr);
      
      // Load day details and billing positions (Abrechnungspositionen)
      const [dayResponse, billingPositionsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/calendar/day/${dateStr}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/billing-positions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setBillingPositions(billingPositionsResponse.data);
      
      // Find team assignment for this employee
      const workDay = workDays.find(wd => wd.date === dateStr);
      const myTeamAssignment = dayResponse.data.team_assignments?.find(
        ta => ta.team_number === workDay?.team_number
      );
      
      // Create order groups structure based on Connect/GWV counts
      if (myTeamAssignment) {
        const connectCount = myTeamAssignment.connect_offtracks || 0;
        const gwvCount = myTeamAssignment.gwv_offtracks || 0;
        
        const groups = [];
        
        // Create Connect order groups
        for (let i = 1; i <= connectCount; i++) {
          groups.push({
            id: `connect-${i}`,
            type: 'connect',
            number: i,
            title: `Connect-Auftrag ${i}`,
            positions: []
          });
        }
        
        // Create GWV order groups
        for (let i = 1; i <= gwvCount; i++) {
          groups.push({
            id: `gwv-${i}`,
            type: 'gwv',
            number: i,
            title: `GWV-Auftrag ${i}`,
            positions: []
          });
        }
        
        setOrderGroups(groups);
      }
      
      // Set billing modal data
      setBillingModalData({
        date: dateStr,
        dayDetails: dayResponse.data,
        workDay: workDay,
        existingBilling: existingBilling || null
      });
      
      // If editing existing billing, load its orders
      if (existingBilling && existingBilling.orders) {
        setBillingOrders(existingBilling.orders);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Tagesdaten:', error);
      alert('Fehler beim Laden der Tagesdaten');
      setShowBillingModal(false);
    } finally {
      setLoadingBillingData(false);
    }
  };
  
  const saveBillingFromModal = async () => {
    // Sammle alle Positionen aus allen Auftrags-Gruppen und strukturiere sie korrekt
    const orders = orderGroups
      .filter(group => group.positions.length > 0)
      .map(group => ({
        order_type: group.type, // 'connect' oder 'gwv'
        order_number: group.number,
        smile_number: `${group.type.toUpperCase()}-${group.number}`, // z.B. "CONNECT-1" oder "GWV-1"
        positions: group.positions.map(pos => ({
          position_id: pos.billing_position_id,
          position_number: pos.position_number,
          description: pos.description,
          unit: pos.unit,
          unit_price: pos.unit_price,
          quantity: pos.quantity,
          subtotal: pos.amount
        })),
        total: group.positions.reduce((sum, pos) => sum + pos.amount, 0)
      }));
    
    if (orders.length === 0) {
      alert('Bitte fügen Sie mindestens eine Position zu einem Auftrag hinzu');
      return;
    }
    
    try {
      const token = localStorage.getItem('authToken');
      const workDay = billingModalData.workDay;
      const existingBilling = billingModalData.existingBilling;
      
      // Get employee_id from workDay or from localStorage
      const employeeId = workDay?.employee_id || localStorage.getItem('employee_id');
      
      if (!employeeId) {
        alert('Fehler: Mitarbeiter-ID nicht gefunden. Bitte melden Sie sich erneut an.');
        return;
      }
      
      const totalAmount = calculateTotalAmount();
      
      const billingPayload = {
        date: selectedDate,
        employee_id: employeeId,
        team_id: workDay?.team_id || '',
        team_number: workDay?.team_number || 0,
        support_teams: [],
        orders: orders,
        total_amount: totalAmount,
        confirmed: false
      };
      
      console.log('Sending billing payload:', JSON.stringify(billingPayload, null, 2));
      
      if (existingBilling) {
        // Update existing billing
        await axios.put(`${BACKEND_URL}/api/billings/${existingBilling.id}`, billingPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Abrechnung erfolgreich aktualisiert und an Buchhaltung gesendet!');
      } else {
        // Create new billing
        await axios.post(`${BACKEND_URL}/api/billings`, billingPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Abrechnung erfolgreich erstellt und an Buchhaltung gesendet!');
      }
      
      // Close modal and reload data with small delay to ensure backend has saved
      setShowBillingModal(false);
      setBillingModalData(null);
      setOrderGroups([]);
      
      // Wait 500ms before reloading to ensure backend has processed the save
      setTimeout(() => {
        loadMonthData();
      }, 500);
    } catch (error) {
      console.error('Fehler beim Speichern der Abrechnung:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Fehler beim Speichern der Abrechnung';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            // Pydantic validation errors
            errorMessage = error.response.data.detail.map(err => 
              `${err.loc?.join('.')} - ${err.msg}`
            ).join('\n');
          } else {
            errorMessage = JSON.stringify(error.response.data.detail, null, 2);
          }
        } else {
          errorMessage = JSON.stringify(error.response.data, null, 2);
        }
      }
      
      alert(errorMessage);
    }
  };
  
  const addPositionToOrder = (orderGroupId, positionId) => {
    const position = billingPositions.find(pos => pos.id === positionId);
    if (position) {
      const updatedGroups = orderGroups.map(group => {
        if (group.id === orderGroupId) {
          return {
            ...group,
            positions: [...group.positions, {
              billing_position_id: position.id,
              description: `${position.position_number} - ${position.description}`,
              position_number: position.position_number,
              unit: position.unit,
              quantity: 1,
              unit_price: position.unit_price,
              amount: position.unit_price
            }]
          };
        }
        return group;
      });
      setOrderGroups(updatedGroups);
    }
  };
  
  const updateOrderPosition = (orderGroupId, positionIndex, field, value) => {
    const updatedGroups = orderGroups.map(group => {
      if (group.id === orderGroupId) {
        const updatedPositions = [...group.positions];
        updatedPositions[positionIndex][field] = value;
        
        // Recalculate amount
        if (field === 'quantity') {
          updatedPositions[positionIndex].amount = 
            updatedPositions[positionIndex].quantity * updatedPositions[positionIndex].unit_price;
        }
        
        return { ...group, positions: updatedPositions };
      }
      return group;
    });
    setOrderGroups(updatedGroups);
  };
  
  const removePositionFromOrder = (orderGroupId, positionIndex) => {
    const updatedGroups = orderGroups.map(group => {
      if (group.id === orderGroupId) {
        return {
          ...group,
          positions: group.positions.filter((_, idx) => idx !== positionIndex)
        };
      }
      return group;
    });
    setOrderGroups(updatedGroups);
  };
  
  const calculateTotalAmount = () => {
    return orderGroups.reduce((total, group) => {
      return total + group.positions.reduce((sum, pos) => sum + pos.amount, 0);
    }, 0);
  };
  
  const addBillingOrder = (positionId) => {
    // Diese Funktion wird nicht mehr benötigt
  };
  
  const addMultipleBillingPositions = (selectedPositionIds) => {
    // Diese Funktion wird nicht mehr benötigt
  };
  
  const updateBillingOrder = (index, field, value) => {
    // Diese Funktion wird nicht mehr benötigt - nutzt updateOrderPosition
  };
  
  const removeBillingOrder = (index) => {
    // Diese Funktion wird nicht mehr benötigt - nutzt removePositionFromOrder
  };

  const submitBilling = async (billing) => {
    if (!window.confirm('Möchten Sie diese Abrechnung zur Prüfung einreichen? Nach der Einreichung können Sie sie nicht mehr bearbeiten.')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${BACKEND_URL}/api/billings/${billing.id}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Abrechnung erfolgreich zur Prüfung eingereicht!');
      loadMonthData(); // Reload data
    } catch (error) {
      console.error('Fehler beim Einreichen:', error);
      alert(error.response?.data?.detail || 'Fehler beim Einreichen der Abrechnung');
    }
  };

  return (
    <div className="min-h-full bg-slate-50 p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Euro className="w-8 h-8 text-emerald-600" />
            Meine Abrechnungen
          </h1>
          <p className="text-slate-600 mt-1">
            {currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
          </p>
        </div>
        <Button variant="outline" onClick={handleRefresh} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Aktualisieren
        </Button>
      </div>

      {/* Statistics Overview - Enhanced and Always Visible */}
      <div className="mb-6">
        <Card className="bg-gradient-to-r from-emerald-50 via-blue-50 to-amber-50 border-2 border-emerald-200 shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-slate-700 flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Abrechnungsstatistik für {currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-4">
              {/* Total */}
              <div className="text-center p-4 bg-white rounded-lg shadow-sm border border-slate-200">
                <p className="text-sm text-slate-600 mb-2 font-medium">📊 Gesamt</p>
                <p className="text-4xl font-bold text-slate-900 mb-1">{stats.total}</p>
                <p className="text-xs text-slate-500">Abrechnungen</p>
              </div>
              
              {/* Approved */}
              <div className="text-center p-4 bg-green-50 rounded-lg shadow-sm border-2 border-green-300">
                <p className="text-sm text-green-800 mb-2 font-medium">✅ Genehmigt</p>
                <p className="text-4xl font-bold text-green-600 mb-1">{stats.approved}</p>
                <p className="text-xs text-green-600">Freigegeben</p>
              </div>
              
              {/* Rejected */}
              <div className="text-center p-4 bg-red-50 rounded-lg shadow-sm border-2 border-red-300">
                <p className="text-sm text-red-800 mb-2 font-medium">❌ Abgelehnt</p>
                <p className="text-4xl font-bold text-red-600 mb-1">{stats.rejected}</p>
                <p className="text-xs text-red-600">Zurückgewiesen</p>
              </div>
              
              {/* Pending/Open */}
              <div className="text-center p-4 bg-amber-50 rounded-lg shadow-sm border-2 border-amber-300">
                <p className="text-sm text-amber-800 mb-2 font-medium">⏳ Offen</p>
                <p className="text-4xl font-bold text-amber-600 mb-1">{stats.pending}</p>
                <p className="text-xs text-amber-600">Zur Prüfung</p>
              </div>
              
              {/* Total Amount */}
              <div className="text-center p-4 bg-emerald-50 rounded-lg shadow-sm border-2 border-emerald-300">
                <p className="text-sm text-emerald-800 mb-2 font-medium">💰 Summe</p>
                <p className="text-4xl font-bold text-emerald-600 mb-1">{stats.totalAmount.toFixed(2)}</p>
                <p className="text-xs text-emerald-600">Euro (netto)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Month Navigation */}
      <div className="mb-6 flex items-center justify-center gap-4">
        <Button variant="outline" size="sm" onClick={previousMonth}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Vorheriger Monat
        </Button>
        <span className="text-lg font-semibold text-slate-700">
          {currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
        </span>
        <Button variant="outline" size="sm" onClick={nextMonth}>
          Nächster Monat
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      {/* Completed Billings Table */}
      {billings.length > 0 && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Abgeschlossene Abrechnungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">📅 Datum</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">📋 Positionen</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">💵 Summe (netto)</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">📌 Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">🗂 Aktion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {billings.sort((a, b) => new Date(b.date) - new Date(a.date)).map((billing) => {
                    const positionsCount = billing.orders?.reduce((sum, order) => sum + order.positions.length, 0) || 0;
                    return (
                      <tr key={billing.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm">
                          {new Date(billing.date).toLocaleDateString('de-DE')}
                        </td>
                        <td className="px-4 py-3 text-sm">{positionsCount}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-emerald-600">
                          {billing.total_amount.toFixed(2)} €
                        </td>
                        <td className="px-4 py-3">
                          {billing.status === 'approved' && (
                            <Badge className="bg-green-100 text-green-800 border-green-300">
                              ✅ Freigegeben
                            </Badge>
                          )}
                          {billing.status === 'rejected' && (
                            <Badge className="bg-red-100 text-red-800 border-red-300">
                              ❌ Abgelehnt
                            </Badge>
                          )}
                          {billing.status === 'submitted' && (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                              ⏳ In Prüfung
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDetails(billing)}
                              className="gap-1"
                            >
                              <Eye className="w-3 h-3" />
                              Details
                            </Button>
                            {billing.status === 'rejected' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => resubmitBilling(billing)}
                                className="gap-1 border-orange-500 text-orange-600 hover:bg-orange-50"
                              >
                                <RefreshCw className="w-3 h-3" />
                                Neu einreichen
                              </Button>
                            )}
                            {billing.status === 'approved' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => exportToPDF(billing)}
                                  className="gap-1"
                                >
                                  <Download className="w-3 h-3" />
                                  PDF
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => exportToExcel(billing)}
                                  className="gap-1"
                                >
                                  <FileSpreadsheet className="w-3 h-3" />
                                  Excel
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Work Days List - All work days with billing status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5" />
            Arbeitstage Übersicht ({workDays.length})
          </CardTitle>
          <p className="text-sm text-slate-600 mt-1">
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              Bereits abgerechnet
            </span>
            <span className="mx-3">•</span>
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 bg-red-500 rounded-full"></span>
              Noch nicht abgerechnet
            </span>
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-500">Lade Arbeitstage...</p>
            </div>
          ) : workDays.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <CalendarIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Keine Arbeitstage in diesem Monat</p>
              <p className="text-slate-400 text-sm mt-1">
                Wählen Sie einen anderen Monat oder überprüfen Sie Ihre Team-Zuweisungen
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {workDays.map((workDay) => {
                const billing = getBillingForDate(workDay.date);
                const hasBilling = workDay.has_billing || !!billing;
                
                return (
                  <Card 
                    key={workDay.date} 
                    className={`transition-shadow ${
                      hasBilling 
                        ? 'border-l-4 border-l-green-500 bg-green-50/30' 
                        : 'border-l-4 border-l-red-500 hover:shadow-md'
                    }`}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-lg font-semibold">
                              {new Date(workDay.date).toLocaleDateString('de-DE', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </span>
                            
                            {hasBilling ? (
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                                <Badge variant={
                                  billing?.status === 'approved' ? 'default' : 
                                  billing?.status === 'submitted' ? 'secondary' :
                                  billing?.status === 'rejected' ? 'destructive' :
                                  'outline'
                                } className="text-sm">
                                  {billing?.status === 'approved' ? (
                                    <>
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Genehmigt
                                    </>
                                  ) : billing?.status === 'submitted' ? (
                                    <>
                                      <Clock className="w-3 h-3 mr-1" />
                                      Zur Prüfung eingereicht
                                    </>
                                  ) : billing?.status === 'rejected' ? (
                                    <>
                                      <AlertCircle className="w-3 h-3 mr-1" />
                                      Abgelehnt - Kann bearbeitet werden
                                    </>
                                  ) : workDay.billing_confirmed ? (
                                    <>
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Genehmigt
                                    </>
                                  ) : (
                                    <>
                                      <FileText className="w-3 h-3 mr-1" />
                                      Abgerechnet
                                    </>
                                  )}
                                </Badge>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                                <Badge variant="destructive">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Keine Abrechnung
                                </Badge>
                              </div>
                            )}
                          </div>

                          <div className="grid grid-cols-3 gap-4 text-sm text-slate-600">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              <span>Team {workDay.team_number}</span>
                            </div>
                            {hasBilling && billing && (
                              <>
                                <div className="flex items-center gap-2">
                                  <FileText className="w-4 h-4" />
                                  <span>{billing.orders?.length || 0} Auftrag(e)</span>
                                </div>
                                <div className="flex items-center gap-2 font-semibold text-emerald-600">
                                  <Euro className="w-4 h-4" />
                                  <span>{billing.total_amount?.toFixed(2) || '0.00'} €</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-2 items-center">
                          {hasBilling ? (
                            <>
                              {billing?.status === 'rejected' && billing?.rejection_reason && (
                                <div className="text-sm text-red-600 mr-4 max-w-xs">
                                  <p className="font-semibold">Ablehnungsgrund:</p>
                                  <p>{billing.rejection_reason}</p>
                                </div>
                              )}
                              
                              {/* Approved billings - show info message */}
                              {(billing?.status === 'approved' || workDay.billing_confirmed) && (
                                <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 px-4 py-2 rounded-lg border-2 border-emerald-300 mr-2 shadow-sm">
                                  <CheckCircle className="w-5 h-5" />
                                  <div>
                                    <span className="font-bold block">✅ Abrechnung genehmigt</span>
                                    <span className="text-xs">Dieser Tag ist bereits abgerechnet und freigegeben</span>
                                  </div>
                                </div>
                              )}
                              
                              {/* Submitted billings - show info message */}
                              {billing?.status === 'submitted' && (
                                <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 px-4 py-2 rounded-lg border-2 border-blue-300 mr-2 shadow-sm">
                                  <Clock className="w-5 h-5" />
                                  <div>
                                    <span className="font-bold block">⏳ Wartet auf Genehmigung</span>
                                    <span className="text-xs">Abrechnung wurde an Buchhaltung gesendet</span>
                                  </div>
                                </div>
                              )}
                              
                              {/* Draft billings - show info message */}
                              {billing?.status === 'draft' && (
                                <div className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 px-4 py-2 rounded-lg border-2 border-slate-300 mr-2 shadow-sm">
                                  <FileText className="w-5 h-5" />
                                  <div>
                                    <span className="font-bold block">📝 Entwurf</span>
                                    <span className="text-xs">Abrechnung noch nicht eingereicht</span>
                                  </div>
                                </div>
                              )}
                              
                              {/* Rejected billings - show info message */}
                              {billing?.status === 'rejected' && (
                                <div className="flex items-center gap-2 text-sm text-orange-700 bg-orange-50 px-4 py-2 rounded-lg border-2 border-orange-300 mr-2 shadow-sm">
                                  <AlertCircle className="w-5 h-5" />
                                  <div>
                                    <span className="font-bold block">❌ Abgelehnt</span>
                                    <span className="text-xs">Kann bearbeitet und erneut eingereicht werden</span>
                                  </div>
                                </div>
                              )}
                              
                              {/* Default billing status when no billing object but has_billing is true */}
                              {!billing && workDay.has_billing && !workDay.billing_confirmed && (
                                <div className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 px-4 py-2 rounded-lg border-2 border-slate-300 mr-2 shadow-sm">
                                  <FileText className="w-5 h-5" />
                                  <div>
                                    <span className="font-bold block">📝 Abgerechnet</span>
                                    <span className="text-xs">Abrechnung wurde erstellt</span>
                                  </div>
                                </div>
                              )}
                              
                              {billing && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openDetails(billing)}
                                >
                                  <FileText className="w-4 h-4 mr-2" />
                                  Details
                                </Button>
                              )}
                              
                              {/* Bearbeiten-Button nur für submitted und rejected */}
                              {(billing?.status === 'submitted' || billing?.status === 'rejected') && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => createBilling(workDay.date)}
                                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                                >
                                  <FileText className="w-4 h-4 mr-2" />
                                  Bearbeiten
                                </Button>
                              )}
                              
                              {(billing?.status === 'approved' || workDay.billing_confirmed) && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => printBilling(billing)}
                                    className="border-blue-500 text-blue-600 hover:bg-blue-50"
                                  >
                                    <Printer className="w-4 h-4 mr-2" />
                                    Drucken
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => exportToPDF(billing)}
                                    className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                                  >
                                    <Download className="w-4 h-4 mr-2" />
                                    PDF
                                  </Button>
                                </>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 px-3 py-2 rounded-lg border border-red-200 mr-2">
                                <AlertCircle className="w-4 h-4" />
                                <span className="font-medium">Noch nicht abgerechnet</span>
                              </div>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => createBilling(workDay.date)}
                                className="bg-emerald-600 hover:bg-emerald-700"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Abrechnung erstellen
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail/Print Modal */}
      {showDetailModal && selectedBilling && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:bg-white print:relative print:p-0">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto print:shadow-none print:max-w-full print:max-h-full">
            {/* Print Header - only visible when printing */}
            <div className="hidden print:block p-8 border-b">
              <div className="text-center mb-4">
                <h1 className="text-3xl font-bold text-slate-900">SKALARTECH</h1>
                <p className="text-slate-600">Abrechnungsdokument</p>
              </div>
            </div>

            {/* Modal Header - hidden when printing */}
            <div className="p-6 border-b print:hidden">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-slate-900">Abrechnung</h2>
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  Schließen
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 print:p-8">
              {/* Date and Status */}
              <div className="mb-6">
                <h3 className="text-xl font-semibold mb-2">
                  {new Date(selectedBilling.date).toLocaleDateString('de-DE', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </h3>
                <Badge variant={selectedBilling.confirmed ? 'default' : 'secondary'} className="print:border print:border-slate-300">
                  {selectedBilling.confirmed ? 'Bestätigt' : 'Ausstehend'}
                </Badge>
              </div>

              {/* Team Info */}
              <div className="mb-6 p-4 bg-slate-50 rounded-lg print:bg-white print:border print:border-slate-300">
                <p className="text-sm text-slate-600">Team</p>
                <p className="font-semibold text-lg">Team {selectedBilling.team_number}</p>
              </div>

              {/* Orders */}
              <div className="mb-6">
                <h4 className="font-semibold mb-3">Aufträge</h4>
                {selectedBilling.orders?.map((order, index) => (
                  <div key={index} className="mb-4 p-4 border rounded-lg print:border-slate-300">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold">
                          {order.order_type === 'gwv' ? 'GWV' : 'Connect'} {order.order_number}
                        </p>
                        <p className="text-sm text-slate-600">SMILE: {order.smile_number}</p>
                      </div>
                      <p className="font-bold text-emerald-600">{order.total?.toFixed(2) || '0.00'} €</p>
                    </div>

                    {/* Positions */}
                    {order.positions?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-slate-700 mb-2">Positionen:</p>
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 print:bg-slate-100">
                            <tr>
                              <th className="text-left p-2">Position</th>
                              <th className="text-right p-2">Menge</th>
                              <th className="text-right p-2">Einzelpreis</th>
                              <th className="text-right p-2">Summe</th>
                            </tr>
                          </thead>
                          <tbody>
                            {order.positions.map((pos, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="p-2">
                                  <span className="font-mono text-xs">{pos.position_number}</span> - {pos.description}
                                </td>
                                <td className="text-right p-2">{pos.quantity} {pos.unit}</td>
                                <td className="text-right p-2">{pos.unit_price?.toFixed(2)} €</td>
                                <td className="text-right p-2 font-semibold">{pos.subtotal?.toFixed(2)} €</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="border-t-2 border-slate-300 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-semibold">Gesamtsumme:</span>
                  <span className="text-2xl font-bold text-emerald-600">
                    {selectedBilling.total_amount?.toFixed(2) || '0.00'} €
                  </span>
                </div>
              </div>

              {/* Print Footer */}
              <div className="hidden print:block mt-12 pt-6 border-t text-center text-sm text-slate-500">
                <p>Gedruckt am: {new Date().toLocaleString('de-DE')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Billing Creation Modal */}
      {showBillingModal && billingModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold text-slate-900">
                Abrechnung erstellen für {new Date(selectedDate).toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Team {billingModalData.workDay?.team_number}
              </p>
            </div>
            
            <div className="p-6 space-y-6">
              {loadingBillingData ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
                  <p className="mt-4 text-slate-600">Lade Daten...</p>
                </div>
              ) : (
                <>
                  {/* Team Assignment Info */}
                  {billingModalData.dayDetails?.team_assignments && (
                    (() => {
                      const myTeamAssignment = billingModalData.dayDetails.team_assignments.find(
                        ta => ta.team_number === billingModalData.workDay?.team_number
                      );
                      
                      if (myTeamAssignment) {
                        const connectOrders = myTeamAssignment.connect_offtracks || 0;
                        const gwvOrders = myTeamAssignment.gwv_offtracks || 0;
                        const totalOrders = connectOrders + gwvOrders;
                        
                        return (
                          <Card className="bg-blue-50 border-blue-200">
                            <CardContent className="pt-4">
                              <div className="flex items-start gap-3">
                                <div className="bg-blue-500 rounded-full p-2">
                                  <Users className="w-5 h-5 text-white" />
                                </div>
                                <div className="flex-1">
                                  <h3 className="font-semibold text-blue-900 mb-2">
                                    Ihrem Team zugewiesene Aufträge am {new Date(selectedDate).toLocaleDateString('de-DE')}
                                  </h3>
                                  <div className="grid grid-cols-3 gap-4 text-sm">
                                    <div className="bg-white rounded p-3 border border-blue-200">
                                      <p className="text-slate-600 mb-1">Connect Aufträge</p>
                                      <p className="text-2xl font-bold text-blue-600">{connectOrders}</p>
                                    </div>
                                    <div className="bg-white rounded p-3 border border-blue-200">
                                      <p className="text-slate-600 mb-1">GWV Aufträge</p>
                                      <p className="text-2xl font-bold text-blue-600">{gwvOrders}</p>
                                    </div>
                                    <div className="bg-white rounded p-3 border border-blue-200">
                                      <p className="text-slate-600 mb-1">Gesamt</p>
                                      <p className="text-2xl font-bold text-emerald-600">{totalOrders}</p>
                                    </div>
                                  </div>
                                  <p className="text-sm text-blue-700 mt-3">
                                    ℹ️ Bitte rechnen Sie alle {totalOrders} Aufträge einzeln ab
                                  </p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      }
                      return null;
                    })()
                  )}
                  
                  {/* Order Groups - Each Connect/GWV Order */}
                  <div className="space-y-4">
                    {orderGroups.map((orderGroup) => (
                      <Card key={orderGroup.id} className="border-2 border-slate-300">
                        <CardHeader className={orderGroup.type === 'connect' ? 'bg-blue-50' : 'bg-green-50'}>
                          <CardTitle className="text-lg flex items-center gap-2">
                            {orderGroup.type === 'connect' ? (
                              <div className="bg-blue-500 text-white rounded px-3 py-1 text-sm font-bold">CONNECT</div>
                            ) : (
                              <div className="bg-green-600 text-white rounded px-3 py-1 text-sm font-bold">GWV</div>
                            )}
                            <span>{orderGroup.title} abrechnen</span>
                            <span className="text-sm text-slate-600 ml-auto">
                              ({orderGroup.positions.length} Position{orderGroup.positions.length !== 1 ? 'en' : ''})
                            </span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                          {/* Position Selector for this order */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Position hinzufügen:</label>
                            <div className="max-h-[200px] overflow-y-auto border border-slate-300 rounded-lg bg-white">
                              {billingPositions && billingPositions.length > 0 ? (
                                <div className="divide-y divide-slate-200">
                                  {billingPositions.map(position => {
                                    // Check if this position is already added to THIS order
                                    const isAdded = orderGroup.positions.some(
                                      pos => pos.billing_position_id === position.id
                                    );
                                    
                                    return (
                                      <div 
                                        key={position.id} 
                                        className={`p-2 transition-colors flex items-center justify-between gap-2 ${
                                          isAdded 
                                            ? 'bg-green-100 border-l-4 border-green-500' 
                                            : 'hover:bg-slate-50'
                                        }`}
                                      >
                                        <div className="flex-1 text-sm">
                                          <div className={`font-medium ${isAdded ? 'text-green-800' : 'text-slate-900'}`}>
                                            {position.position_number} - {position.description}
                                          </div>
                                          <div className={`text-xs ${isAdded ? 'text-green-700' : 'text-slate-600'}`}>
                                            {position.unit_price.toFixed(2)} € / {position.unit}
                                          </div>
                                        </div>
                                        <Button
                                          size="sm"
                                          onClick={() => addPositionToOrder(orderGroup.id, position.id)}
                                          disabled={isAdded}
                                          className={isAdded 
                                            ? "bg-green-600 hover:bg-green-600 cursor-not-allowed" 
                                            : "bg-emerald-600 hover:bg-emerald-700"}
                                        >
                                          {isAdded ? (
                                            <>
                                              <CheckCircle className="w-3 h-3 mr-1" />
                                              Hinzugefügt
                                            </>
                                          ) : (
                                            <>
                                              <Plus className="w-3 h-3 mr-1" />
                                              Hinzufügen
                                            </>
                                          )}
                                        </Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="p-4 text-center text-sm text-slate-500">
                                  Keine Abrechnungspositionen verfügbar
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Added Positions for this order */}
                          {orderGroup.positions.length === 0 ? (
                            <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed">
                              <FileText className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                              <p className="text-sm text-slate-600">Noch keine Positionen hinzugefügt</p>
                              <p className="text-xs text-slate-500 mt-1">Wählen Sie oben eine Position aus</p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <h4 className="text-sm font-semibold text-slate-700 mb-2">Hinzugefügte Positionen:</h4>
                              {orderGroup.positions.map((position, posIdx) => (
                                <Card key={posIdx} className="p-3 bg-slate-50">
                                  <div className="grid grid-cols-12 gap-2 items-center text-sm">
                                    <div className="col-span-5">
                                      <div className="font-medium text-slate-800">{position.description}</div>
                                    </div>
                                    <div className="col-span-2">
                                      <Input
                                        type="number"
                                        value={position.quantity}
                                        onChange={(e) => updateOrderPosition(orderGroup.id, posIdx, 'quantity', parseFloat(e.target.value) || 0)}
                                        min="0"
                                        step="0.5"
                                        className="h-8 text-sm"
                                      />
                                    </div>
                                    <div className="col-span-2 text-center">
                                      <div className="text-slate-700">{position.unit_price.toFixed(2)} €</div>
                                    </div>
                                    <div className="col-span-2 text-center">
                                      <div className="font-semibold text-emerald-600">{position.amount.toFixed(2)} €</div>
                                    </div>
                                    <div className="col-span-1 text-right">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removePositionFromOrder(orderGroup.id, posIdx)}
                                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  {/* Total Amount */}
                  {orderGroups.some(g => g.positions.length > 0) && (
                    <Card className="bg-emerald-50 border-emerald-200">
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-semibold">Gesamtsumme aller Aufträge:</span>
                          <span className="text-2xl font-bold text-emerald-600">
                            {calculateTotalAmount().toFixed(2)} €
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3 justify-end pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowBillingModal(false);
                        setBillingModalData(null);
                        setOrderGroups([]);
                      }}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Abbrechen
                    </Button>
                    <Button
                      variant="default"
                      onClick={saveBillingFromModal}
                      disabled={orderGroups.every(g => g.positions.length === 0)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Speichern und an Buchhaltung senden
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyBillings;
